import { useEffect, useState } from 'react'
import { useWindowContext } from '../WindowContext'
import { MaximizeIcon } from '../icons/MaximizeIcon'
import { RestoreIcon } from '../icons/RestoreIcon'
import './WindowControls.scss'

/**
 * WindowControls - Windows-spezifische Fenstersteuerung (Minimize, Maximize, Close)
 *
 * Rendert die drei Window Control Buttons im Windows-Stil.
 * Der Maximize-Button wechselt automatisch zwischen Maximize und Restore.
 *
 * @example
 * ```tsx
 * // Wird intern von Titlebar gerendert (nur auf Windows):
 * {platform === 'win32' && <WindowControls />}
 * ```
 */
export const WindowControls = () => {
  const windowProps = useWindowContext().window
  const [isMaximized, setIsMaximized] = useState(false)

  const minimizePath = 'M 0,5 10,5 10,6 0,6 Z'
  const closePath =
    'M 0,0 0,0.7 4.3,5 0,9.3 0,10 0.7,10 5,5.7 9.3,10 10,10 10,9.3 5.7,5 10,0.7 10,0 9.3,0 5,4.3 0.7,0 Z'

  const showMinimize = windowProps?.minimizable !== false
  const showMaximize = windowProps?.maximizable !== false

  useEffect(() => {
    if (!window.api) return

    const handleMaximize = () => setIsMaximized(true)
    const handleUnmaximize = () => setIsMaximized(false)

    window.api.receive('window-maximized', handleMaximize)
    window.api.receive('window-unmaximized', handleUnmaximize)
    window.api.invoke('window-is-maximized').then(setIsMaximized).catch(() => {})

    return () => {
      window.api.removeAllListeners('window-maximized')
      window.api.removeAllListeners('window-unmaximized')
    }
  }, [])

  return (
    <div className={`titlebar__window-controls ${isMaximized ? 'titlebar__window-controls--maximized' : ''}`}>
      {showMinimize && (
        <ControlButton label="minimize" svgPath={minimizePath} />
      )}
      {showMaximize && (
        <MaximizeButton isMaximized={isMaximized} />
      )}
      <ControlButton label="close" svgPath={closePath} />
    </div>
  )
}

const MaximizeButton = ({ isMaximized }: { isMaximized: boolean }) => {
  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation()
    window.api?.invoke('window-maximize-toggle').catch(() => {})
  }

  return (
    <div
      aria-label="maximize"
      className="titlebar__control"
      onClick={handleAction}
    >
      {isMaximized ? <RestoreIcon size={11} /> : <MaximizeIcon size={10} />}
    </div>
  )
}

const ControlButton = ({
  svgPath,
  label
}: {
  svgPath: string;
  label: string
}) => {
  const { onClose } = useWindowContext().titlebar

  const handleAction = (e: React.MouseEvent) => {
    e.stopPropagation()

    switch (label) {
      case 'minimize':
        window.api?.invoke('window-minimize').catch(() => {})
        break
      case 'close':
        if (onClose) {
          onClose()
        } else {
          window.api?.invoke('window-close').catch(() => {})
        }
        break
      default:
        console.warn(`Unhandled action for label: ${label}`)
    }
  }

  const modifierClass = label === 'close' ? 'titlebar__control--close' : ''

  return (
    <div
      aria-label={label}
      className={`titlebar__control ${modifierClass}`}
      onClick={handleAction}
    >
      <svg width="10" height="10" viewBox="0 0 10 10">
        <path d={svgPath} fill="currentColor" />
      </svg>
    </div>
  )
}
